import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header/Header';
import HeroSection from './components/HeroSection/HeroSection';
import ProductsOverview from './components/ProductsOverview/ProductsOverview';
import ShopSection from './components/ShopSection/ShopSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer/Footer';
import CartSidebar from './components/CartSidebar/CartSidebar';
import Login from './components/Login/Login';
import Register from './components/Register/Register';
import SplashScreen from './components/SplashScreen/SplashScreen';
import CheckoutForm from './components/CheckoutForm/CheckoutForm';
import GalleryAndTestimonials from './components/GalleryAndTestimonials/GalleryAndTestimonials';
import Categories from './components/Categories';

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [ackMessage, setAckMessage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortOption, setSortOption] = useState('price-asc');
  const [user, setUser] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [products] = useState([
    // Hardcoded sample products since Firebase is removed
    { id: '1', name: 'Bulk Rice (10kg)', category: 'groceries', price: 15, quantityPricePairs: { '10kg': 15 }, popularity: 5 },
    { id: '2', name: 'Cola Case (24-pack)', category: 'beverages', price: 10, quantityPricePairs: { '24-pack': 10 }, popularity: 8 },
    { id: '3', name: 'Cleaning Wipes (100-pack)', category: 'household', price: 8, quantityPricePairs: { '100-pack': 8 }, popularity: 3 },
  ]);

  // Categories for cash and carry in the UK
  const categories = [
    { id: 'groceries', name: 'Groceries', image: 'https://via.placeholder.com/150?text=Groceries', productCount: 20 },
    { id: 'beverages', name: 'Beverages', image: 'https://via.placeholder.com/150?text=Beverages', productCount: 15 },
    { id: 'household', name: 'Household Items', image: 'https://via.placeholder.com/150?text=Household', productCount: 10 },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const addToCart = (product, selectedQty, price) => {
    if (!user) {
      setShowLogin(true);
      return;
    }
    const validQty = selectedQty && product.quantityPricePairs[selectedQty]
      ? selectedQty
      : Object.keys(product.quantityPricePairs)[0];
    const newItem = {
      product: { ...product },
      selectedQty: validQty,
      multiplier: 1
    };
    const updatedCart = [...cart];
    const existingItemIndex = updatedCart.findIndex(
      (item) => item.product.id === product.id && item.selectedQty === validQty
    );
    if (existingItemIndex > -1) {
      updatedCart[existingItemIndex].multiplier += 1;
    } else {
      updatedCart.push(newItem);
    }
    setCart(updatedCart);
    setAckMessage(`${product.name} (${validQty}) added to cart`);
    setTimeout(() => setAckMessage(null), 3000);
  };

  const updateQuantity = (productId, selectedQty, change) => {
    const updatedCart = cart
      .map((item) => {
        if (item.product.id === productId && item.selectedQty === selectedQty) {
          const newMultiplier = (item.multiplier || 1) + change;
          return newMultiplier > 0 ? { ...item, multiplier: newMultiplier } : null;
        }
        return item;
      })
      .filter(Boolean);
    setCart(updatedCart);
  };

  const removeFromCart = (productId, selectedQty) => {
    const updatedCart = cart.filter(
      (item) => !(item.product.id === productId && item.selectedQty === selectedQty)
    );
    setCart(updatedCart);
  };

  const totalAmount = cart.reduce((sum, item) => {
    const unitPrice = item.product.quantityPricePairs[item.selectedQty] || 0;
    const multiplier = item.multiplier || 1;
    return sum + unitPrice * multiplier;
  }, 0);

  const handleCategoryChange = (category) => setSelectedCategory(category);
  const handleSortChange = (option) => setSortOption(option);

  const handleCheckout = (orderDetails) => {
    console.log('Order Details:', orderDetails);
    setShowCheckout(false);
    setCart([]);
    setAckMessage('Order placed successfully! Please collect your items.');
    setTimeout(() => setAckMessage(null), 3000);
  };

  const filteredProducts =
    selectedCategory === 'all'
      ? products
      : products.filter((product) => product.category === selectedCategory);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortOption) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'newest':
        return b.id - a.id;
      case 'popularity':
        return b.popularity - a.popularity;
      default:
        return 0;
    }
  });

  return (
    <div className="min-vh-100 bg-light">
      {showSplash && <SplashScreen />}
      {!showSplash && (
        <>
          <Header
            cart={cart}
            setIsCartOpen={setIsCartOpen}
            user={user}
            setShowLogin={setShowLogin}
          />
          <HeroSection />
          <ProductsOverview />
          <ShopSection
            products={sortedProducts}
            addToCart={addToCart}
            filterCategory={selectedCategory}
            handleCategoryChange={handleCategoryChange}
            sortOption={sortOption}
            handleSortChange={handleSortChange}
          />
          <GalleryAndTestimonials />
          <AboutSection />
          <ContactSection />
          <Footer />
          {ackMessage && <p>{ackMessage}</p>}
          <CartSidebar
            isCartOpen={isCartOpen}
            setIsCartOpen={setIsCartOpen}
            cart={cart}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            totalAmount={totalAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
            setShowCheckout={setShowCheckout}
          />
          {showLogin && (
            <div className="modal show d-block">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Login</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowLogin(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <Login
                      setUser={setUser}
                      setShowLogin={setShowLogin}
                      setShowRegister={setShowRegister}
                      setAckMessage={setAckMessage}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {showRegister && (
            <div className="modal show d-block">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Register</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowRegister(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <Register
                      setUser={setUser}
                      setShowLogin={setShowLogin}
                      setShowRegister={setShowRegister}
                      setAckMessage={setAckMessage}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {showCheckout && (
            <div className="modal show d-block">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Checkout</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowCheckout(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <CheckoutForm
                      cart={cart}
                      totalAmount={totalAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                      handleCheckout={handleCheckout}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {ackMessage && (
            <div className="fixed-bottom alert alert-success d-flex align-items-center justify-content-between">
              <span>{ackMessage}</span>
              <button className="btn btn-success" onClick={() => setIsCartOpen(true)}>
                Go to Cart
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;